import isEqual from 'lodash/isEqual';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withFormContext } from '../FormContext';
import ControlWrapper from '../ControlWrapper';

class FormTableRowData extends Component {
  shouldComponentUpdate(newProps) {
    const { form, canDelete } = this.props;

    const fieldNames = Object.keys(form);

    if (newProps.canDelete !== canDelete) {
      return true;
    }

    for (const fieldName of fieldNames) {
      if (newProps.form[fieldName].control === 'checkbox') {
        return true;
      }

      if (newProps.form[fieldName].calculated) {
        return true;
      }

      for (const key of ['hidden', 'valid', 'disabled', 'originalValue', 'touched', 'calculated']) {
        if (!isEqual(form[fieldName][key], newProps.form[fieldName][key])) {
          return true;
        }
      }
    }

    return false;
  }

  getVisibleColumns() {
    const { columns, form } = this.props;

    const visibleColumns = [];

    columns.forEach((fieldName) => {
      if (form[fieldName].hidden) {
        return;
      }

      visibleColumns.push(form[fieldName]);
    });

    return visibleColumns;
  }

  computeStyle(field, columnIndex) {
    const { form } = this.props;
    const columns = this.getVisibleColumns();
    const style = {};
    let width;
    let widthCalculated = false;
    const columnLength = columns.length;

    // Don't count leading checkboxes
    // if (columns[0].control === 'checkbox' && columnLength !== 1) {
    //   columnLength--;
    // }

    // Less than 4 columns
    // Just split the size of the fields into equal parts for small collections
    // if (columnLength <= 4) {
    //   if (columnIndex === 0 &&  columns[0].control === 'Checkbox') {
    //     style.width = 1;
    //     style.minWidth = 1;
    //     widthCalculated = true;
    //   } else {
    //     style.width = `${100 / columnLength}%`;
    //     widthCalculated = true;
    //   }
    //   return { style, widthCalculated };
    // }

    // Compute smart sizing limits based on what we know about the field (unless it's the last column)
    if (field.width) {
      style.width = field.width;
      style.minWidth = field.width;
      widthCalculated = true;
    } else if (field.type === 'date') {
      style.minWidth = 100;
      style.width = 100;
      widthCalculated = true;
    } else if (form[field.name].control === 'checkbox') {
      style.width = 1;
      style.minWidth = 1;
      widthCalculated = true;
    } else if (field.maxLen && !field.options && !field.validation) {
      if (field.maxLen <= 5) {
        width = field.maxLen * 15;
      } else {
        width = field.maxLen * 7;
      }
      style.width = width;
      style.minWidth = width > 100 ? width / 4 : width;
      widthCalculated = true;
    } else if (field.validation) {
      let validationWidth = 150;
      if (field.validation.toolbar) {
        validationWidth += field.validation.toolbar.length * 20;
      }
      style.minWidth = validationWidth;
    }

    if (columnIndex + 1 !== columnLength) {
      delete style.width;
      widthCalculated = false;
    }

    return { style, widthCalculated };
  }

  renderItem(field) {
    const { form, rowId } = this.props;
    const key = `${field.name}_${rowId}`;
    const controlId = `${form[field.name].identifier}-${rowId}`;

    const ControlComponent = this.props.context.manager.controls[form[field.name].control.toLowerCase()];

    return (
      <ControlWrapper controlId={controlId} field={form[field.name]}>
        <ControlComponent
          key={key}
          field={field}
          controlId={controlId}
          value={form[field.name].displayValue}
          originalValue={form[field.name].originalDisplayValue}
          options={form[field.name].options}
          valid={form[field.name].valid}
          errorMessage={form[field.name].errorMessage}
          hidden={form[field.name].hidden}
          disabled={form[field.name].disabled}
          touched={form[field.name].touched}
          warning={form[field.name].warning}
          info={form[field.name].info}
          calculated={form[field.name].calculated}
          style={form[field.name].style}
          onChange={(fieldName, value, cb) => {
            const dataIndex = this.props.getDataIndex(rowId);
            this.props.onChange(fieldName, value, dataIndex, cb);
          }}
          onClick={(fieldName) => {
            const dataIndex = this.props.getDataIndex(rowId);
            return this.props.onClick(fieldName, dataIndex, rowId);
          }}
          onBlur={(fieldName) => {
            const dataIndex = this.props.getDataIndex(rowId);
            return this.props.onBlur(fieldName, dataIndex, rowId);
          }}
          setWarning={(fieldName, warning) => {
            const dataIndex = this.props.getDataIndex(rowId);
            return this.props.setWarning(fieldName, warning, dataIndex);
          }}
          setInfo={(fieldName, message) => {
            const dataIndex = this.props.getDataIndex(rowId);
            return this.props.setInfo(fieldName, message, dataIndex);
          }}
          control={form[field.name].control}
          search={form[field.name].validation}
          noLabel
          {...field.props}
        />
      </ControlWrapper>
    );
  }

  render() {
    const { rowId, columns, form } = this.props;
    const result = [];

    columns.forEach((fieldName, columnIndex) => {
      const field = form[fieldName] || {};
      // const columnHidden = field.hiddenIndex.every(d => d === true);
      const columnHidden = field.allHidden;

      if (field.hidden) {
        if (columnHidden) {
          result.push(<td key={`rowdata_${field.identifier}_${rowId}`} className="is-hidden" />);
        } else {
          result.push(<td key={`rowdata_${field.identifier}_${rowId}`} />);
        }
        return;
      }

      const { style, widthCalculated } = this.computeStyle(field, columnIndex);

      const className = classNames('column-field', {
        'text-center': form[field.name].control.toLowerCase() === 'checkbox',
        'width-calculated': widthCalculated,
        'is-last': columnIndex + 1 === columns.length,
      });

      result.push(
        <td key={`rowdata_${field.identifier}_${rowId}`} style={style} className={className}>
          {this.renderItem(field, field.identifier)}
        </td>
      );
    });
    return result;
  }
}

FormTableRowData.defaultProps = {
  columns: [],
  form: {},
  getDataIndex: () => {},
  onBlur: () => {},
  onChange: () => {},
  onClick: () => {},
  setInfo: () => {},
  setWarning: () => {},
};

FormTableRowData.propTypes = {
  canDelete: PropTypes.bool.isRequired,
  columns: PropTypes.array,
  context: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  getDataIndex: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  rowId: PropTypes.number.isRequired,
  setInfo: PropTypes.func,
  setWarning: PropTypes.func,
};

export default withFormContext(FormTableRowData);
